import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { Loading01Icon, AlertCircleIcon, CheckCircleIcon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'
import { useOpeningHours } from '@/hooks/api'
import { Switch } from '@/components/ui/Switch'

interface OpeningHoursSectionProps {
  className?: string
}

/**
 * Opening Hours Section - displays opening hours from Google Places
 * when a Google Place ID is selected and the toggle is enabled.
 */
export function OpeningHoursSection({ className }: OpeningHoursSectionProps) {
  const { t } = useTranslation()
  const { setValue, control } = useFormContext()
  
  // Watch required form values
  const googlePlaceId = useWatch({ control, name: 'config.google_place_id' })
  const showOpeningHours = useWatch({ control, name: 'config.show_opening_hours' })
  const timezone = useWatch({ control, name: 'timezone' }) || 'Europe/Amsterdam'

  // Fetch opening hours from API
  const {
    data: openingHours,
    isLoading,
    error,
  } = useOpeningHours(googlePlaceId, timezone, showOpeningHours && !!googlePlaceId)

  // Don't show section if no Google Place is selected
  if (!googlePlaceId) {
    return null
  }

  const handleToggle = (isSelected: boolean) => {
    setValue('config.show_opening_hours', isSelected, { shouldDirty: true })
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {/* Section header */}
      <div>
        <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100">
          {t('resources.assistants.opening_hours.title')}
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.opening_hours.description')}
        </p>
      </div>

      {/* Toggle switch */}
      <div className="flex items-center gap-3">
        <Switch
          isSelected={showOpeningHours || false}
          onChange={handleToggle}
        >
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('resources.assistants.opening_hours.use_google')}
          </span>
        </Switch>
      </div>

      {/* Loading state */}
      {showOpeningHours && isLoading && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
          <Loading01Icon className="h-4 w-4 animate-spin" />
          {t('resources.assistants.opening_hours.loading')}
        </div>
      )}

      {/* Error state */}
      {showOpeningHours && error && (
        <div className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <AlertCircleIcon className="h-4 w-4" />
          {t('resources.assistants.opening_hours.error')}
        </div>
      )}

      {/* Opening hours display */}
      {showOpeningHours && openingHours && !isLoading && !error && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800">
          <pre className="whitespace-pre-wrap font-mono text-sm text-gray-600 dark:text-gray-300">
            {openingHours}
          </pre>
        </div>
      )}
    </div>
  )
}

export default OpeningHoursSection
